/**
 * BookingSummarySidebar Component
 * Renders all non-calendar context for the booking flow (meeting identity,
 * duration, who you're meeting, location, timezone, and the chosen time).
 * On desktop this sits to the LEFT of the interactive step so the calendar
 * gets the full right pane; on mobile it stacks above the step content.
 */

import React from 'react';
import { Clock, Users, User, Video, MapPin, CalendarCheck } from 'lucide-react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Card, CardContent } from '../../card';
import { BookingData } from '../utils/bookingConstants';
import { formatTimeZoneAbbreviation } from '../utils/bookingTimezones';
import { EventTypeConfig } from '../hooks/useEventTypeConfig';
import { TimeZonePicker } from './TimeZonePicker';

interface BookingSummarySidebarProps {
  bookingData: BookingData;
  eventType: string | null;
  config: EventTypeConfig;
  /** Change the zone every displayed time is rendered in. */
  onTimeZoneChange: (zone: string) => void;
}

function emailToName(email: string): string {
  return email
    .split('@')[0]
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function BookingSummarySidebar({
  bookingData,
  eventType,
  config,
  onTimeZoneChange,
}: BookingSummarySidebarProps) {
  const meetingName = (eventType && config.name) || 'Book a Meeting';
  const description = config.description || bookingData.description || null;

  const teamCount = config.groupEmails.length;
  const whoLabel = eventType
    ? teamCount > 0
      ? `${teamCount} team member${teamCount !== 1 ? 's' : ''} available`
      : 'WTC team'
    : bookingData.bookeeEmail
      ? emailToName(bookingData.bookeeEmail)
      : 'Not selected yet';

  const isOnline = bookingData.isOnline ?? config.isOnline ?? true;
  const locationLabel = isOnline
    ? 'Google Meet (online)'
    : bookingData.location || config.location || 'In person';

  const slot = bookingData.selectedSlot;
  const timeZone = bookingData.timeZone;
  // Resolve the abbreviation against the MEETING, not today, so a booking that
  // crosses a DST boundary is labelled with the offset it will actually happen
  // in (e.g. a November meeting booked in October reads CST, not CDT).
  const abbreviationReference = slot ? parseISO(slot.start) : undefined;

  return (
    <div className="lg:sticky lg:top-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-card-foreground mb-2">{meetingName}</h2>
        {description && <p className="text-sm text-gray-600 leading-relaxed">{description}</p>}
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex items-center gap-3 text-gray-700">
          <Clock className="w-4 h-4 text-blue-600 shrink-0" />
          <span>{bookingData.meetingDuration} minutes</span>
        </div>

        <div className="flex items-center gap-3 text-gray-700">
          {eventType && teamCount > 0 ? (
            <Users className="w-4 h-4 text-blue-600 shrink-0" />
          ) : (
            <User className="w-4 h-4 text-blue-600 shrink-0" />
          )}
          <span>{whoLabel}</span>
        </div>

        <div className="flex items-center gap-3 text-gray-700">
          {isOnline ? (
            <Video className="w-4 h-4 text-blue-600 shrink-0" />
          ) : (
            <MapPin className="w-4 h-4 text-blue-600 shrink-0" />
          )}
          <span>{locationLabel}</span>
        </div>

      </div>

      {/* Time zone control. Every time on the page renders in this zone, so it
          lives with the meeting facts rather than buried in a settings menu. */}
      <TimeZonePicker
        value={timeZone}
        onChange={onTimeZoneChange}
        referenceDate={abbreviationReference}
      />

      {slot && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <CalendarCheck className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-blue-700 mb-1">
                  Selected Time
                </div>
                <div className="font-medium text-card-foreground">
                  {formatInTimeZone(parseISO(slot.start), timeZone, 'EEEE, MMMM d')}
                </div>
                <div className="text-sm text-gray-600">
                  {formatInTimeZone(parseISO(slot.start), timeZone, 'h:mm a')} –{' '}
                  {formatInTimeZone(parseISO(slot.end), timeZone, 'h:mm a')}{' '}
                  <span className="font-medium text-gray-700">
                    {formatTimeZoneAbbreviation(timeZone, abbreviationReference)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
